/*
 * The Router's project-record gate: what a `?projectId` route renders while `state.projectData` loads. The slice
 * holds ONE project; on a switch from project A to project B it still holds A until B's read lands, so a gate that
 * passed on "any project stored" would render B's route with A's name, cover, members and custom fields. The route
 * renders only once the slice holds the project the URL names.
 */

/** The members of `ShellGate` (AppShellNext/routeKind.ts), declared here so classic code imports no shell file. */
export type ProjectGate = "ready" | "forbidden" | "loading";

/** The two members of `state.projectData` the gate reads. */
export interface ProjectGateRecord {
  _id?: string | undefined;
  isDataFetched: boolean;
}

/**
 * @param requestedId the route's `?projectId` as the URL carries it (null / "" = no project in scope)
 * @param loaded `state.projectData`
 * @returns "ready" when no project is requested or the requested one is stored; "forbidden" when the read for it
 *   found nothing (`getCurrProjectData` stored `{}`); else "loading" (nothing read yet, or another project still
 *   stored while the requested one is read).
 */
export function projectGate(requestedId: string | null, loaded: ProjectGateRecord): ProjectGate {
  if (!requestedId) return "ready";
  if (loaded._id) return sameObjectId(loaded._id, requestedId) ? "ready" : "loading";
  return loaded.isDataFetched ? "forbidden" : "loading";
}

/**
 * The Router reads the query through `objectIdString`, which trims and accepts upper-case hex, while the stored id
 * is the server's lower-case one: compared on the same normal form, so such a link is not left loading forever.
 */
function sameObjectId(storedId: string, requestedId: string): boolean {
  return storedId.toLowerCase() === requestedId.trim().toLowerCase();
}
